import { CharacterBase } from './characters/CharacterBase';
import { CharacterEnemy } from './characters/CharacterEnemy';
import { CharacterCyberGorilla } from './characters/bosses/CharacterCyberGorilla';
import { CharacterDragon } from './characters/bosses/CharacterDragon';
import { CharacterForeman } from './characters/bosses/CharacterForeman';
import { CharacterSheriff } from './characters/bosses/CharacterSheriff';
import { CharacterUltralak } from './characters/bosses/CharacterUltralak';

export const BOSS_MAP = {
  DRAGON: CharacterDragon,
  GORILLA: CharacterCyberGorilla,
  SHERIFF: CharacterSheriff,
  FOREMAN: CharacterForeman,
  ULTRALAK: CharacterUltralak,
};

export class EnemyController extends Phaser.Events.EventEmitter {
  constructor(scene, renderer, collisionmap) {
    super();

    this._scene = scene;
    this._worldRenderer = renderer;
    this._enemies = [];
    this._collisionmap = collisionmap;
    this._combatzones = false;
    this._activeWave = false;
    this._hero = false;
    this._enemySpawns = [];
    this._activeBoss = false;
    this._activeBossWaveDelay = 0;
  }

  get enemies() {
    return [...this._enemies];
  }

  get activeBoss() {
    return this._activeBoss;
  }

  get activeEnemies() {
    return this.activeBoss ? [...this.enemies, this.activeBoss] : [...this.enemies];
  }

  get hasActiveEnemies() {
    return this.enemies?.length || this.activeBoss || this._activeWave ? true : false;
  }

  setHeroTarget(value) {
    this._hero = value;
    this._enemies.forEach((e) => e.setHeroTarget(this._hero));
  }

  setCombatZones(combatzones) {
    this._combatzones = [...combatzones];
  }

  setEnemySpawns(enemySpawns) {
    this._enemySpawns = [...enemySpawns];
  }

  clearEnemies() {
    this._clearEnemiesOnNextUpdate = true;
  }

  update(delta, viewport) {
    // if no active combat zone, check for next one
    if (!this._activeCombatZone && this._combatzones?.length) {
      this.checkCombatZoneTrigger(viewport);
    }

    if (this._clearEnemiesOnNextUpdate) {
      this._enemies.forEach((e) => e.destroy());
      this._enemies = [];
      this._clearEnemiesOnNextUpdate = false;
    }

    //update boss

    if (this._activeBoss) {
      this._activeBoss.update(delta, viewport);
      if (this._activeBoss.isFullyDead) {
        this._activeBoss.destroy();
        this._activeBoss = null;
        // we don;t want to spawn any more enemies if boss defeated
        if (this._activeWave) this._activeWave.length = 0;
      }
    }

    // if active combat zone, but no enemeies - spawn next wave
    if (this._activeWave && !this._enemies.length) {
      if (this._activeWave.length) {
        if (this._activeBossWaveDelay <= 0) this.spawnEnemyWave(this._activeWave.shift(), viewport);
      } else if (!this._activeBoss) this.completeCombatZone();
    }

    if (this._activeBossWaveDelay > 0) this._activeBossWaveDelay -= delta;

    // check spawn locations
    for (let i = this._enemySpawns.length - 1; i >= 0; i--) {
      const spawn = this._enemySpawns[i];
      if (viewport.contains(spawn.x, spawn.y)) {
        this._enemySpawns.splice(i, 1);
        this.spawnEnemy(spawn.type, spawn.x, spawn.y);
      }
    }

    // update any active enemies

    for (let i = this._enemies.length - 1; i >= 0; i--) {
      const enemy = this._enemies[i];
      enemy.update(delta);

      // if the enemy is in a knockdown state, check for wall bounce
      if (enemy.isKnockedDown) {
        const bounds = enemy._sprite.getBounds();
        if (
          (enemy.velocity.x > 0 && bounds.offset.x + bounds.size.x > viewport.right) ||
          (enemy.velocity.x < 0 && bounds.offset.x < viewport.left)
        ) {
          enemy.velocity.x *= -1;
          enemy.velocity.z = Math.max(1000, enemy.velocity.z);
        }
      }

      if (enemy.isFullyDead) {
        this._scene.events.emit(EnemyController.ENEMY_DEFEATED, enemy);
        enemy.destroy();
        this._enemies.splice(i, 1);
      }
    }
  }

  spawnEnemyWave(wave, viewport) {
    const { left, right, top, height } = viewport;
    const placementsLeft = this._collisionmap.getTilesInWorldRect(
      {
        x: left - 64,
        y: top,
        width: 128,
        height,
      },
      true
    );
    const placementsRight = this._collisionmap.getTilesInWorldRect(
      {
        x: right - 64,
        y: top,
        width: 128,
        height,
      },
      true
    );
    Phaser.Utils.Array.Shuffle(placementsLeft);
    Phaser.Utils.Array.Shuffle(placementsRight);
    wave.forEach((entry) => {
      let count = entry.qty || 1;
      let placements = entry.direction === 'LEFT' ? placementsLeft : placementsRight;
      while (count--) {
        this.spawnEnemy(entry.enemyid, placements[0].x, placements[0].y);
        placements.shift();
      }
    });
  }

  spawnEnemy(enemyid, x, y) {
    const model = this._scene.game.model.getEnemy(enemyid);
    if (!model) return;
    const enemy = new CharacterEnemy(this._scene, x, y, model, this._collisionmap);
    if (this._worldRenderer) this._worldRenderer.addEnemyCharacter(enemy);
    enemy.setHeroTarget(this._hero);
    this._enemies.push(enemy);
    return enemy;
  }

  spawnBoss(bossid, viewport) {
    if (this._activeBoss || !BOSS_MAP[bossid]) return;

    const { left, right, top, height } = viewport;
    const validPositions = this._collisionmap.getTilesInWorldRect(
      {
        x: right - 32,
        y: top,
        width: 64,
        height,
      },
      true
    );
    const pos = validPositions[Math.floor(Math.random() * validPositions.length)];
    this._activeBoss = new BOSS_MAP[bossid](this._scene, pos.x, pos.y, {}, this._collisionmap);
    this._activeBoss.setHeroTarget(this._hero);
    if (this._worldRenderer) this._worldRenderer.addEnemyCharacter(this._activeBoss);
  }

  clearBoss() {
    if (!this._activeBoss) return;

    this._activeBoss.destroy();
    this._activeBoss = null;
  }

  // =============================================================================================
  // COMBAT ZONE MANAGEMET
  // =============================================================================================

  checkCombatZoneTrigger(viewport) {
    if (!this._combatzones || !viewport) return;
    const { left, right, centerX } = viewport;
    const isEndOfStage = this._scene.hasReachedLevelEnd;
    for (let i = 0; i < this._combatzones.length; i++) {
      const { x, width } = this._combatzones[i];
      if (isEndOfStage && x + width <= right) {
        this.activateCombatZone(this._combatzones[i], viewport);
        this._combatzones.splice(i, 1);
        break;
      } else {
        if (x > right) continue;
        if (x <= left || x + width * 0.5 <= centerX) {
          this.activateCombatZone(this._combatzones[i], viewport);
          this._combatzones.splice(i, 1);
          break;
        }
      }
    }
  }

  activateCombatZone(zone, viewport) {
    this._activeCombatZone = zone;
    const zoneconfig = this._scene.game.model.getCombatZone(this._activeCombatZone.name);
    this._activeWave = [...zoneconfig.enemyWaves];
    this._scene.events.emit(EnemyController.COMBAT_ZONE_START, this._activeCombatZone);
    if (zoneconfig.boss) {
      this._activeBossWaveDelay = zoneconfig.bossWaveDelay || 10;
      this.spawnBoss(zoneconfig.boss, viewport);
    }
  }

  completeCombatZone() {
    this._scene.events.emit(EnemyController.COMBAT_ZONE_END, this._activeCombatZone);
    this._activeCombatZone = false;
    this._activeWave = false;
  }
}

EnemyController.COMBAT_ZONE_START = 'COMBAT_ZONE_ACTIVATED';
EnemyController.COMBAT_ZONE_END = 'COMBAT_ZONE_COMPLETE';
EnemyController.ENEMY_DEFEATED = 'ENEMY_DEFEATED';
